import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { saveReturnPath } from "../auth";
import { useSurfLibrary, type SurfDeckRecord } from "../hooks/useSurfLibrary";
import { useSurfAssetUrls } from "../hooks/useSurfAssetUrls";
import { speakerReaderPath } from "../utils/speakerReaderLink";
import { SlideThumb } from "../components/editor/SlideThumb";
import { EmptyState } from "../ui/EmptyState";

function DeckCard({
  row,
  onRename,
  onDelete,
  onDuplicate,
}: {
  row: SurfDeckRecord;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(row.deck.title);
  const { backgroundUrls, layerImageUrls } = useSurfAssetUrls(row.deck);
  const firstSlide = row.deck.slides[0];

  const commitRename = () => {
    const next = title.trim();
    if (next && next !== row.deck.title) onRename(row.documentId, next);
    setEditing(false);
  };

  return (
    <article className="deck-card deck-card--with-cover">
      {firstSlide ? (
        <div className="deck-card-cover" aria-hidden>
          <SlideThumb
            deck={row.deck}
            slide={firstSlide}
            slideIndex={0}
            slideBackgroundUrl={backgroundUrls[firstSlide.id]}
            layerImageUrls={layerImageUrls}
            onClick={() => undefined}
          />
        </div>
      ) : (
        <div className="deck-card-cover deck-card-cover--empty muted">Vuoto</div>
      )}
      {editing ? (
        <input
          className="deck-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => commitRename()}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
          }}
          autoFocus
        />
      ) : (
        <h2>
          <Link to={`/deck/${row.documentId}`}>{row.deck.title}</Link>
        </h2>
      )}
      <p className="muted">
        {row.deck.slides.length} slide · aggiornato{" "}
        {new Date(row.deck.contentUpdatedAt).toLocaleString()}
      </p>
      <div className="deck-actions">
        <Link
          to={speakerReaderPath(row.documentId)}
          className="deck-action-link"
          onClick={() => saveReturnPath(speakerReaderPath(row.documentId))}
        >
          Lettore speaker
        </Link>
        <button type="button" onClick={() => setEditing(true)}>
          Rinomina
        </button>
        <button type="button" onClick={() => onDuplicate(row.documentId)}>
          Duplica
        </button>
        <button type="button" className="danger" onClick={() => onDelete(row.documentId)}>
          Elimina
        </button>
      </div>
    </article>
  );
}

export function LibraryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const spaceHandled = useRef(false);
  const {
    decks,
    status,
    error,
    ready,
    createDeck,
    renameDeck,
    deleteDeck,
    duplicateDeck,
    refresh,
  } = useSurfLibrary();

  const spaceId = searchParams.get("spaceId") ?? searchParams.get("projectId");

  useEffect(() => {
    if (!ready || !spaceId || spaceHandled.current) return;
    spaceHandled.current = true;
    void createDeck("Nuova presentazione", spaceId).then((row) => {
      navigate(`/deck/${row.documentId}/wizard`, { replace: true });
    });
  }, [ready, spaceId, createDeck, navigate]);

  return (
    <div className="library-page surf-shell-page">
      <header className="shell-header">
        <h1>Libreria</h1>
        <p className="muted">Presentazioni Surf</p>
      </header>

      <div className="library-toolbar">
        <button
          type="button"
          className="primary-btn"
          disabled={!ready}
          onClick={() =>
            void createDeck().then((row) => navigate(`/deck/${row.documentId}/wizard`))
          }
        >
          Nuova presentazione
        </button>
        <button type="button" disabled={!ready} onClick={() => void refresh()}>
          Sincronizza
        </button>
        <span className="muted">Sync: {status}</span>
      </div>

      {error && <p className="error">{error}</p>}

      {!ready && <p className="muted">Caricamento libreria…</p>}

      <div className="deck-grid">
        {decks.map((row) => (
          <DeckCard
            key={row.documentId}
            row={row}
            onRename={(id, title) => void renameDeck(id, title)}
            onDelete={(id) => void deleteDeck(id)}
            onDuplicate={(id) => void duplicateDeck(id)}
          />
        ))}
      </div>

      {ready && decks.length === 0 && (
        <EmptyState
          title="Nessuna presentazione"
          description="Crea la tua prima presentazione AI con il wizard guidato."
          action={
            <button
              type="button"
              className="primary-btn"
              onClick={() =>
                void createDeck().then((row) => navigate(`/deck/${row.documentId}/wizard`))
              }
            >
              Nuova presentazione
            </button>
          }
        />
      )}
    </div>
  );
}
